<template>
	<ul class="pc-search-dynamic-list">
		<li
			v-for="(item, index) in items"
			:key="item.id"
			:ref="el => bindDynamicResultImpression(el, item, index)"
			class="pc-search-dynamic-list__card"
		>
			<div class="pc-search-dynamic-list__header">
				<div class="pc-search-dynamic-list__author">
					<img
						class="pc-search-dynamic-list__avatar"
						:src="getPublisherInfo(item).avatar"
						:alt="getPublisherInfo(item).nickname"
						loading="lazy"
						decoding="async"
					/>
					<span class="pc-search-dynamic-list__name">{{ getPublisherInfo(item).nickname }}</span>
				</div>
				<div class="pc-search-dynamic-list__header-actions">
					<button
						type="button"
						class="pc-search-dynamic-list__follow-btn"
						:class="{ 'pc-search-dynamic-list__follow-btn--followed': isItemFollowed(item) }"
						:disabled="isFollowLoading(item.id)"
						@click.stop="handleFollow(item)"
					>
						{{ isItemFollowed(item) ? t("square.detail.followed") : t("square.detail.follow") }}
					</button>
				</div>
			</div>

			<button
				type="button"
				class="pc-search-dynamic-list__body"
				@click="handleNavigate(item, index)"
			>
				<p v-if="getDynamicText(item)" class="pc-search-dynamic-list__title">
					{{ getDynamicText(item) }}
				</p>
				<img
					v-if="item.cover"
					class="pc-search-dynamic-list__cover"
					:src="item.cover"
					:alt="item.title || ''"
					loading="lazy"
					decoding="async"
				/>
			</button>

			<div class="pc-search-dynamic-list__actions">
				<button
					type="button"
					class="pc-search-dynamic-list__action"
					:aria-label="t('common.pcSearchModal.dynamic.comment')"
					@click.stop="handleComment(item)"
				>
					<img :src="commentIcon" alt="" aria-hidden="true" />
				</button>
				<button
					type="button"
					class="pc-search-dynamic-list__action"
					:aria-label="t('common.pcSearchModal.dynamic.share')"
					@click.stop="handleComment(item)"
				>
					<img :src="shareIcon" alt="" aria-hidden="true" />
				</button>
				<button
					type="button"
					class="pc-search-dynamic-list__action"
					:class="{ 'pc-search-dynamic-list__action--active': isItemZaned(item) }"
					:aria-label="t('square.detail.like')"
					:disabled="isZanLoading(item.id)"
					@click.stop="handleZan(item)"
				>
					<img :src="isItemZaned(item) ? zanAfterIcon : zanBeforeIcon" alt="" aria-hidden="true" />
				</button>
			</div>
		</li>
	</ul>
</template>

<script setup>
import { inject, ref } from "vue";
import { likeSquare, trackSquare } from "~/api/api";
import { useI18n } from "~/composables/useI18n";
import { GLOBAL_SEARCH_RESULT_TYPES } from "~/composables/useSiteComponentTracking";
import { useShare } from "~/hooks/share/index";
import showMessage from "~/utils/showMessage";
import commentIcon from "@variants/seer/square-detail/images/pinlun.png";
import shareIcon from "@variants/seer/square-detail/images/sahre.png";
import zanAfterIcon from "@variants/seer/square-detail/images/zan-after.png";
import zanBeforeIcon from "@variants/seer/square-detail/images/zan-before.png";
import moreIcon from "@variants/seer/square-detail/images/more.png";

const props = defineProps({
	items: {
		type: Array,
		default: () => [],
	},
	keywordType: {
		type: String,
		default: undefined,
	},
});

const emit = defineEmits(["navigate"]);

const { t, tm } = useI18n();
const openLoginPopup = inject("openLoginPopup", null);
const globalSearchBindImpression = inject("globalSearchBindImpression", null);

const followLoadingMap = ref({});
const zanLoadingMap = ref({});

const getFollowState = data => data?.is_follow === true || data?.is_follow === 1;
const getZanState = data => data?.is_zan === true || data?.is_zan === 1;

function getPublisherInfo(item) {
	const info = item?.publisher_info;
	return {
		avatar: info?.avatar || item?.avatar || "",
		nickname: info?.nickname || item?.nickname || item?.author || "",
		uid: item?.uid || info?.uid || "",
	};
}

function getDynamicText(item) {
	return item?.title || item?.desc || "";
}

function isItemFollowed(item) {
	return getFollowState(item);
}

function isItemZaned(item) {
	return getZanState(item);
}

function isFollowLoading(id) {
	return !!followLoadingMap.value[String(id)];
}

function isZanLoading(id) {
	return !!zanLoadingMap.value[String(id)];
}

function checkLogin() {
	if (!import.meta.client) return false;
	if (!localStorage.getItem("token")) {
		openLoginPopup?.();
		return false;
	}
	return true;
}

function bindDynamicResultImpression(el, item, index) {
	if (!el || !item?.id || typeof globalSearchBindImpression !== "function") return;
	globalSearchBindImpression(
		el,
		`result_${GLOBAL_SEARCH_RESULT_TYPES.CONTENT}_${item.id}`,
		"global_search_result_impression",
		{
			result_type: GLOBAL_SEARCH_RESULT_TYPES.CONTENT,
			result_id: String(item.id),
			position: index + 1,
			keyword_type: props.keywordType,
		}
	);
}

function handleNavigate(item, index) {
	if (!item?.id) return;
	emit("navigate", item, index);
}

function handleComment(item, index) {
	handleNavigate(item, index);
}

async function handleFollow(item) {
	if (!import.meta.client || isFollowLoading(item.id)) return;
	if (!checkLogin()) return;

	const uid = getPublisherInfo(item).uid;
	if (!uid) return;

	const itemId = String(item.id);
	followLoadingMap.value = { ...followLoadingMap.value, [itemId]: true };
	const prevFollowed = isItemFollowed(item);
	item.is_follow = !prevFollowed;

	try {
		const res = await trackSquare({ uid: String(uid) });
		const result = res?.data?.[0];
		if (result) {
			item.is_follow = getFollowState(result);
		}
	} catch (error) {
		item.is_follow = prevFollowed;
		console.error("追蹤失败:", error);
		showMessage(t("square.detail.actionFailed"), "error");
	} finally {
		const next = { ...followLoadingMap.value };
		delete next[itemId];
		followLoadingMap.value = next;
	}
}

async function handleZan(item) {
	if (!import.meta.client || !item?.id || isZanLoading(item.id)) return;
	if (!checkLogin()) return;

	const itemId = String(item.id);
	zanLoadingMap.value = { ...zanLoadingMap.value, [itemId]: true };
	const prevZaned = isItemZaned(item);
	item.is_zan = !prevZaned;

	try {
		await likeSquare({ square_id: String(item.id) });
	} catch (error) {
		item.is_zan = prevZaned;
		console.error("点赞失败:", error);
		showMessage(t("square.detail.actionFailed"), "error");
	} finally {
		const next = { ...zanLoadingMap.value };
		delete next[itemId];
		zanLoadingMap.value = next;
	}
}

function handleShare(item) {
	if (!import.meta.client || !item?.id) return;

	const origin = window.location.origin;
	const link = item.share_link || `${origin}/seer/square-detail/${item.id}`;
	const texts = tm("square.detail.shareTexts");
	const text =
		Array.isArray(texts) && texts.length ? texts[Math.floor(Math.random() * texts.length)] : "";
	const shareItems = useShare({
		link,
		text,
		img: item.cover,
	});
	const shareItem = shareItems?.[0];

	if (shareItem?.shareFn) {
		shareItem.shareFn();
		return;
	}

	navigator.clipboard
		?.writeText(link)
		.then(() => showMessage(t("square.detail.copyLinkSuccess"), "success"))
		.catch(() => showMessage(t("square.detail.copyLinkFailed"), "error"));
}
</script>

<style lang="scss" scoped>
.pc-search-dynamic-list {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 0;
	list-style: none;

	&__card {
		border: 1px solid #ececec;
		border-radius: 12px;
		background: #ffffff;
		overflow: hidden;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		padding: 16px 16px 12px;
	}

	&__author {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
		flex: 1;
	}

	&__avatar {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		border-radius: 50%;
		object-fit: cover;
		background: #f5f5f5;
	}

	&__name {
		min-width: 0;
		font-size: 16px;
		font-weight: 600;
		color: #333333;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__header-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	&__follow-btn {
		min-width: 88px;
		height: 36px;
		padding: 0 16px;
		border: none;
		border-radius: 8px;
		background: #111111;
		font-size: 18px;
		font-weight: 500;
		color: #ffffff;
		cursor: pointer;
		white-space: nowrap;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	&__body {
		display: block;
		width: 100%;
		padding: 0 16px 12px;
		border: none;
		background: transparent;
		text-align: left;
		cursor: pointer;
	}

	&__title {
		margin: 0 0 12px;
		font-size: 16px;
		line-height: 1.5;
		color: #000000;
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
	}

	&__cover {
		display: block;
		width: 100%;
		max-height: 220px;
		border-radius: 8px;
		object-fit: cover;
		background: #f5f5f5;
	}

	&__actions {
		display: flex;
		align-items: center;
		justify-content: space-around;
		padding: 12px 16px 16px;
	}

	&__action {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 40px;
		padding: 0;
		border: none;
		border-radius: 8px;
		background: transparent;
		cursor: pointer;

		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}

		img {
			width: 32px;
			height: 32px;
			object-fit: contain;
		}

		&--active img {
			filter: none;
		}
	}
}
</style>
